import store from "@/store/index";
import AppSettings from "@/globals";

export default class BaseService {
  constructor() {
    this.baseUrl = "/delegate/yithome-api";

    // Maximum length of a GET request URL before a large buildingtaxonomy
    // filter is split into several requests whose results are merged. The
    // request travels browser -> Azure APIM (IIS/HTTP.sys) -> Kontent.ai and
    // the tightest limit in that chain is somewhere in Azure of
    // 8192 bytes (longer URLs are rejected with HTTP 414). Because the whole
    // taxonomy list ends up in the query string we cap the total URL well
    // below 8192 to leave headroom for the base path and encoding. Override
    // via env when the backend limits are known to differ.
    this.maxUrlLength =
      parseInt(process.env.VUE_APP_KENTICO_DELIVERY_MAX_URL_LENGTH, 10) || 8000;
  }

  /**
   * Housing Company id lives in User state
   */
  getHcId() {
    let hc = store.state.User.selectedHousingCompany;

    if(!hc)
      return null;

    if(hc.liferayId && this.routeViaLiferay())
      return hc.liferayId;

    return hc.id;
  }

  /**
   * Get active language code
   * @return String lang code
   */
  getLanguage() {
    const loggedIn = store.getters.isLogged;
    let lang = store.getters.getLang;
    if (!loggedIn) lang = AppSettings.locale
    return lang ? lang : 'en';
  }

  /**
   * Build url from baseUrl and params object
   *
   * @param {*} baseUrl
   * @param {*} params
   * @param {Boolean} hasQueryString - Uri already has some query parameters defined
   */
  _buildUrl(baseUrl, params = {}, hasQueryString = false) {
    let first = true;
    let query = baseUrl;
    Object.keys(params).map(param => {
      if (
        typeof params[param] !== "undefined" ||
        typeof params[param] !== "null"
      )
        if (first && !hasQueryString) {
          query += `?${param}=${params[param]}`;
          first = false;
        } else query += `&${param}=${params[param]}`;
    });

    return query;
  }

  /**
   * Check if we user Liferay
   *
   */
  routeViaLiferay() {
    let routeViaLiferay = store.getters.getTokenIssuer != 'b2c' ? true : false;
    return routeViaLiferay;
  }

  /**
   * Returns the effective list of buildingtaxonomy terms that will be applied
   * to a Kontent.ai request, normalised to an array. Returns null when no
   * taxonomy filtering is applied.
   *
   * @param Object params
   * @returns Array|null
   */
  _resolveBuildingTaxonomies(params = {}) {
    let value = null;
    if (params.buildingTaxonomies) {
      value = params.buildingTaxonomies;
    } else if (this.targetByDefaultBuildingTaxonomy) {
      value = store.getters.getBuildingTaxonomyFilters;
    }

    if (value === null || value === undefined) return null;

    let terms = Array.isArray(value) ? value : String(value).split(",");
    terms = terms.map((t) => String(t).trim()).filter((t) => t.length > 0);

    return terms.length ? terms : null;
  }

  /**
   * Greedily packs taxonomy terms into chunks so that each resulting URL stays
   * within `maxUrlLength`.
   *
   * @param Number baseLength length of the URL without any taxonomy terms
   * @param Array terms
   * @returns Array<Array<String>>
   */
  _chunkTaxonomyTerms(baseLength, terms = []) {
    const budget = this.maxUrlLength - baseLength;
    const chunks = [];
    let current = [];
    let currentLength = 0;

    terms.forEach((term) => {
      const value = String(term);
      const addLength = (current.length ? 1 : 0) + value.length; // comma + term

      if (current.length && currentLength + addLength > budget) {
        chunks.push(current);
        current = [value];
        currentLength = value.length;
      } else {
        current.push(value);
        currentLength += addLength;
      }
    });

    if (current.length) chunks.push(current);

    return chunks;
  }

  /**
   * Merges several Kontent.ai responses (delivery or preview) into a single
   * axios-like response: de-duplicates items by system id, merges
   * modular_content, re-applies the requested ordering and window, and
   * rebuilds pagination.
   *
   * @param Array responses
   * @param String|null orderField the value used in the `order` query param
   *                     (e.g. "publish_date", "elements.x" or "system.x")
   * @param String orderDirection
   * @param Number skip
   * @param Number limit
   * @returns Object
   */
  _mergeKontentResponses(responses, orderField, orderDirection, skip, limit) {
    const itemsById = new Map();
    let modularContent = {};
    const perChunkLimit = skip + limit;
    let anyChunkTruncated = false;

    responses.forEach((response) => {
      const data = (response && response.data) || {};
      const items = data.items || [];

      if (items.length >= perChunkLimit) anyChunkTruncated = true;

      items.forEach((item) => {
        const id = item && item.system ? item.system.id : null;
        const key = id || JSON.stringify(item);
        if (!itemsById.has(key)) itemsById.set(key, item);
      });

      if (data.modular_content) {
        modularContent = Object.assign({}, modularContent, data.modular_content);
      }
    });

    let items = Array.from(itemsById.values());

    if (orderField && orderField != "none") {
      items.sort(this._buildKontentOrderComparator(orderField, orderDirection));
    }

    const totalMerged = items.length;
    const paged = items.slice(skip, skip + limit);

    const hasMore =
      paged.length === limit && (totalMerged > skip + limit || anyChunkTruncated);

    const lastResponse = responses[responses.length - 1] || {};

    return Object.assign({}, lastResponse, {
      data: {
        items: paged,
        modular_content: modularContent,
        pagination: {
          skip,
          limit,
          count: paged.length,
          next_page: hasMore ? "chunked" : "",
        },
      },
    });
  }

  /**
   * Builds a comparator for the Kontent.ai field used in the `order` query
   * parameter, matching the server-side ordering as closely as possible. The
   * field may be a bare element codename ("publish_date"), or prefixed with
   * "elements." or "system.".
   *
   * @param String orderField
   * @param String direction
   * @returns Function
   */
  _buildKontentOrderComparator(orderField, direction) {
    const dir = String(direction || "desc").toLowerCase() === "asc" ? 1 : -1;

    let source = "elements";
    let field = String(orderField);
    if (field.startsWith("system.")) {
      source = "system";
      field = field.slice("system.".length);
    } else if (field.startsWith("elements.")) {
      field = field.slice("elements.".length);
    }

    const valueOf = (item) => {
      if (!item) return null;
      let value;
      if (source === "system") {
        value = item.system ? item.system[field] : undefined;
      } else {
        value =
          item.elements && item.elements[field]
            ? item.elements[field].value
            : undefined;
      }
      return value === "" || value === undefined || value === null
        ? null
        : value;
    };

    return (a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);

      if (va === null && vb === null) return 0;
      if (va === null) return 1; // nulls last
      if (vb === null) return -1;

      const na = Number(va);
      const nb = Number(vb);
      if (va !== "" && vb !== "" && !isNaN(na) && !isNaN(nb)) {
        return (na - nb) * dir;
      }

      const da = Date.parse(va);
      const db = Date.parse(vb);
      if (!isNaN(da) && !isNaN(db)) return (da - db) * dir;

      return String(va).localeCompare(String(vb)) * dir;
    };
  }
}
